/**
 * ALOHANOVA UNIVERSE - About Page
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Crown, Sparkles, Globe, Heart, Users, Award } from 'lucide-react';
import Enhanced8KGalaxyBackground from '../components/Enhanced8KGalaxyBackground';

const About = () => {
  return (
    <div className="min-h-screen bg-gray-900">
      <Enhanced8KGalaxyBackground quality="high" />
      
      <div className="relative z-10 container mx-auto px-6 py-20 max-w-5xl">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Crown className="w-12 h-12 text-yellow-400" />
            <h1 className="text-6xl font-bold text-white">Alohanova Universe</h1>
            <Crown className="w-12 h-12 text-yellow-400" />
          </div>
          <p className="text-2xl text-purple-300 mb-4">Queen Supernova AI Platform</p>
          <p className="text-xl text-gray-300">Abbreviation: <span className="text-yellow-400 font-bold">QSN</span></p>
        </div>

        {/* Mission */}
        <div className="bg-gray-800/70 backdrop-blur-xl rounded-2xl p-8 mb-8 border border-purple-500/30">
          <h2 className="text-3xl font-bold text-white mb-4 flex items-center gap-2">
            <Sparkles className="w-8 h-8 text-yellow-400" />
            Our Mission
          </h2>
          <p className="text-gray-200 text-lg leading-relaxed">
            Alohanova Universe is the world's first AI platform dedicated to preserving and teaching Hawaiian culture, 
            language (Olelo Hawaii), and traditions through cutting-edge 3D AI avatar technology. We combine artificial 
            intelligence with authentic Hawaiian wisdom to create an immersive educational and interactive experience.
          </p>
        </div>

        {/* What We Offer */}
        <div className="bg-gray-800/70 backdrop-blur-xl rounded-2xl p-8 mb-8 border border-blue-500/30">
          <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-2">
            <Globe className="w-8 h-8 text-blue-400" />
            What We Offer
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-900/50 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-yellow-400 mb-3">🌺 Hawaiian Language Learning</h3>
              <ul className="text-gray-300 space-y-2">
                <li>• Olelo Hawaii lessons with AI tutors</li>
                <li>• Interactive pronunciation guides</li>
                <li>• Cultural context and traditions</li>
                <li>• Native speaker voice synthesis</li>
              </ul>
            </div>
            
            <div className="bg-gray-900/50 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-blue-400 mb-3">🤖 3D AI Avatar Technology</h3>
              <ul className="text-gray-300 space-y-2">
                <li>• Photorealistic Hawaiian avatars</li>
                <li>• Real-time voice synthesis</li>
                <li>• Natural conversations</li>
                <li>• Emotion recognition</li>
              </ul>
            </div>
            
            <div className="bg-gray-900/50 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-purple-400 mb-3">💎 Premium Services</h3>
              <ul className="text-gray-300 space-y-2">
                <li>• Custom avatar creation (50 tokens)</li>
                <li>• Voice cloning (100 tokens)</li>
                <li>• API access for developers</li>
                <li>• Priority support</li>
              </ul>
            </div>
            
            <div className="bg-gray-900/50 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-green-400 mb-3">📚 Educational Content</h3>
              <ul className="text-gray-300 space-y-2">
                <li>• Hawaiian history and culture</li>
                <li>• Traditional chants and songs</li>
                <li>• Cultural protocols (kapu)</li>
                <li>• Genealogy (moʻokūʻauhau)</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Subscription Tiers */}
        <div className="bg-gray-800/70 backdrop-blur-xl rounded-2xl p-8 mb-8 border border-yellow-500/30">
          <h2 className="text-3xl font-bold text-white mb-6">Subscription Plans</h2>
          
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            <div className="bg-blue-900/30 p-6 rounded-xl border border-blue-500/50">
              <h3 className="text-2xl font-bold text-white mb-2">Basic</h3>
              <p className="text-3xl font-bold text-blue-400 mb-4">$9.99<span className="text-lg">/mo</span></p>
              <ul className="text-gray-300 text-sm space-y-2">
                <li>✓ Basic AI chat</li>
                <li>✓ Hawaiian lessons</li>
                <li>✓ 50 prompts/day</li>
              </ul>
            </div>
            
            <div className="bg-purple-900/30 p-6 rounded-xl border border-purple-500/50">
              <h3 className="text-2xl font-bold text-white mb-2">Pro</h3>
              <p className="text-3xl font-bold text-purple-400 mb-4">$49.99<span className="text-lg">/mo</span></p>
              <ul className="text-gray-300 text-sm space-y-2">
                <li>✓ Everything in Basic</li>
                <li>✓ 3D Avatar Chat</li>
                <li>✓ Voice synthesis</li>
                <li>✓ Unlimited prompts</li>
              </ul>
            </div>
            
            <div className="bg-yellow-900/30 p-6 rounded-xl border border-yellow-500/50">
              <h3 className="text-2xl font-bold text-white mb-2">Enterprise</h3>
              <p className="text-3xl font-bold text-yellow-400 mb-4">$99.99<span className="text-lg">/mo</span></p>
              <ul className="text-gray-300 text-sm space-y-2">
                <li>✓ Everything in Pro</li>
                <li>✓ API Access</li>
                <li>✓ Custom training</li>
                <li>✓ White label</li>
              </ul>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 p-6 rounded-xl border-2 border-yellow-400">
              <div className="bg-yellow-400 text-gray-900 text-xs font-bold px-3 py-1 rounded-full inline-block mb-2">BEST VALUE</div>
              <h3 className="text-2xl font-bold text-white mb-2">Ultimate Annual</h3>
              <p className="text-4xl font-bold text-yellow-400 mb-4">$999<span className="text-lg">/year</span></p>
              <p className="text-green-400 mb-4">Save $200+ vs monthly</p>
              <ul className="text-gray-200 text-sm space-y-2">
                <li>✓ Z.AI SDK Access</li>
                <li>✓ Custom avatar creation</li>
                <li>✓ Batch processing</li>
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-yellow-900/50 to-orange-900/50 p-6 rounded-xl border-2 border-yellow-400">
              <div className="bg-yellow-400 text-gray-900 text-xs font-bold px-3 py-1 rounded-full inline-block mb-2">PAY ONCE</div>
              <h3 className="text-2xl font-bold text-white mb-2">Ultimate Lifetime</h3>
              <p className="text-4xl font-bold text-yellow-400 mb-4">$2,999<span className="text-lg"> once</span></p>
              <p className="text-green-400 mb-4">Save $9,000+ over 10 years</p>
              <ul className="text-gray-200 text-sm space-y-2">
                <li>✓ Everything included</li>
                <li>✓ Never pay again</li>
                <li>✓ Lifetime updates</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Notes */}
        <div className="bg-red-900/30 backdrop-blur-xl rounded-2xl p-8 border border-red-500/50">
          <h2 className="text-2xl font-bold text-white mb-4">⚠️ Important Compliance Notice</h2>
          <div className="text-gray-200 space-y-2">
            <p className="font-semibold">❌ We DO NOT offer:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Cryptocurrency trading or sales</li>
              <li>NFT minting, trading, or marketplaces</li>
              <li>Blockchain transactions</li>
              <li>Financial investment services</li>
              <li>Gambling or games of chance</li>
            </ul>
            <p className="font-semibold mt-4">✅ We ARE:</p>
            <p>A legitimate SaaS AI technology and Hawaiian education platform providing 100% digital services.</p>
          </div>
        </div>

        {/* Founder */}
        <div className="bg-gray-800/70 backdrop-blur-xl rounded-2xl p-8 mt-8 border border-purple-500/30 text-center">
          <Crown className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-white mb-2">King Milkman</h2>
          <p className="text-xl text-purple-300 mb-4">Founder & Creator</p>
          <p className="text-gray-300 max-w-2xl mx-auto">
            Malcolm KH Lee III - Hawaiian-Polynesian Waliʻi Royalty, Creator of the Alohanova Universe, 
            dedicated to preserving Hawaiian culture and advancing AI technology.
          </p>
          <p className="text-gray-400 text-sm mt-4">Contact: malcolmlee3@gmail.com</p>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Link
            to="/signup"
            className="inline-block px-10 py-4 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white text-xl font-bold rounded-xl transition transform hover:scale-105 shadow-xl"
          >
            ʻAloha! Get Started Today
          </Link>
        </div>
      </div>
    </div>
  );
};

export default About;